import moment, { Moment } from 'moment';
import { useState } from 'react';
import noop from 'utils/noop';
import { combineDateAndTime } from 'utils/date';
import { guessCurrentTimeZone } from 'utils/timeZone';
import { validateRelativeDate } from 'utils/validateDate';
import { SingleDateCalendar } from './calendar/SingleDateCalendar';
import { DateInput } from './datePickerInput/DateInput';
import { DatePickerInput } from './datePickerInput/DatePickerInput';
import { TimeInput } from './datePickerInput/TimeInput';
import { TimeZonePicker } from './timeZonePicker/TimeZonePicker';
import {
  InputBar,
  SingleDatePickerWrapper,
  TimeZonePickerWrapper,
} from './singleDatePickerComponents/SingleDatePicker.styles';

import { SingleDateFooter, RelativeDatePanel } from './singleDatePickerComponents';
import { convertMomentToDateString, convertValidDateRange } from './momentToDateConversionHelpers';
import { SingleDatePickerProps } from './types';

export const SingleDatePicker = ({
  date,
  defaultTimeZone = guessCurrentTimeZone(),
  hasError = false,
  hideTimeZonePicker = false,
  isRelativeDate = false,
  isTimeZonePickerDisabled = false,
  onDateChange = noop,
  onRelativeDateToggle = noop,
  onTimeZoneChange = noop,
  showTime = false,
  useRelativeDate = false,
  validDateRange,
  relativeDateMenuValues,
  ...props
}: SingleDatePickerProps) => {
  const [currentDate, setCurrentDate] = useState<Moment | string>(date);
  const [timeZone, setTimeZone] = useState(defaultTimeZone);

  const [hasRelativeError, setHasRelativeError] = useState(false);

  const setRelativeDate = (value: string) => {
    setCurrentDate(value);
    setHasRelativeError(validateRelativeDate(value));
    onDateChange(value);
  };

  const handleSetCalendarDate = (newDate: Date) => {
    // Ensures that time value of time input is preserved
    const combinedDateAndTime = combineDateAndTime(moment(newDate), currentDate as Moment);
    setCurrentDate(combinedDateAndTime);
    onDateChange(combinedDateAndTime);
  };

  const handleSetDate = (newDate: Moment) => {
    setCurrentDate(newDate);
    onDateChange(newDate);
  };

  const handleTimeZoneChange = (newTimeZone: string[]) => {
    const [timeZoneValue] = newTimeZone;
    setTimeZone(timeZoneValue);
    onTimeZoneChange(timeZoneValue);
  };

  return (
    <SingleDatePickerWrapper {...props}>
      {/* Shows the relative date input text or the date picker input  */}
      {isRelativeDate && useRelativeDate ? (
        <RelativeDatePanel
          hasRelativeError={hasRelativeError || hasError}
          setRelativeDate={setRelativeDate}
          currentDate={currentDate}
          relativeDateMenuValues={relativeDateMenuValues}
        />
      ) : (
        <>
          <InputBar>
            <DatePickerInput>
              <DateInput date={currentDate} onDateChange={handleSetDate} id="date-input" />
              {showTime && (
                <TimeInput time={currentDate} onTimeChange={handleSetDate} id="time-input" />
              )}
            </DatePickerInput>
            {showTime && !hideTimeZonePicker && (
              <TimeZonePickerWrapper>
                <TimeZonePicker
                  isDisabled={isTimeZonePickerDisabled}
                  timeZone={timeZone}
                  onTimeZoneChange={handleTimeZoneChange}
                />
              </TimeZonePickerWrapper>
            )}
          </InputBar>
          <SingleDateCalendar
            validDateRange={convertValidDateRange(validDateRange)}
            date={convertMomentToDateString(currentDate)}
            onDateChange={handleSetCalendarDate}
          />
        </>
      )}
      {useRelativeDate && (
        <SingleDateFooter
          isRelative={isRelativeDate}
          setIsRelative={onRelativeDateToggle}
          setCurrentDate={setCurrentDate}
        />
      )}
    </SingleDatePickerWrapper>
  );
};
